import IconCopy from '@mui/icons-material/FileCopyOutlined'
import Button from '@mui/material/Button'
import { PPDialog } from 'components/PPDialog'
import Tooltip from '@mui/material/Tooltip'
import { NOTIFICATION_DURATION_LONG } from 'components/ConnectedSnackbar'
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { of as observableOf, Subject } from 'rxjs'
import { catchError, mergeMap } from 'rxjs/operators'
import { checkTokens } from 'services/auth'
import { activeProductsSelector, endTrial, getProductByHandle, upgradeProductV2 } from 'services/product'
import { downgradeFree, getAccountInfo } from 'services/settings'
import { message } from 'services/snackbar'
import { setTrialPopupOpen } from 'services/ui/actions'
import { trialPopupForceOpenWithHandleSelector } from 'services/ui/selectors'
import { getTrialTasks } from 'services/users/actions'
import { currentUserSelector, isTrialActiveSelector } from 'services/users/selectors'
import { StoreThunkDispatch } from 'store/state'
import styles from './TrialPopups.pcss'
import { PPProduct } from 'shared'

const Clipboard = require('clipboard')

// Trial gate — must sit above other modals and snackbars.
const DIALOG_Z_INDEX = 1400

export function EndTrialPopup() {
  const intl = useIntl()
  const navigate = useNavigate()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const plans = useSelector(activeProductsSelector)
  const freePlan = plans.find(p => p.order === 0) as PPProduct
  const openWithPlan = useSelector(trialPopupForceOpenWithHandleSelector)
  const user = useSelector(currentUserSelector)
  const isTrialActive = useSelector(isTrialActiveSelector)
  const currency = user?.account.currency as string || 'usd'

  const [selectedProduct, setSelectedProduct] = useState({
    handle: 'prod-400-monthly',
    isAnnual: false
  })
  const [loading, setLoading] = useState(Boolean(openWithPlan.handle))
  const [isUpgrading, setUpgrading] = useState(false)
  // TEMP: hardcode coupon code const [fetchCoupon, coupon] = useAsyncAction(() => Observable.fromPromise(dispatch(fetchCouponCode())))
  const coupon = { code: '10DISCOUNT101', percent: 10 }

  const checkStatus$ = useRef<Subject<any>>(undefined)
  const clipboard = useRef(null)

  const getClipboardText = () => coupon?.code || ''

  useEffect(() => {
    // fetchCoupon()
    checkStatus$.current = new Subject()
    checkStatus$.current.pipe(
      mergeMap(() => {
        return dispatch(checkTokens())
          .pipe(catchError((error) => observableOf(error)))
      }))
      .subscribe(() => { setLoading(false) })

    if (openWithPlan.handle) {
      checkStatus$.current.next(null)
    }

    return () => {
      checkStatus$.current?.unsubscribe()
    }
  }, [dispatch, openWithPlan.handle])

  const fetchTasks = useCallback(() => {
    dispatch(getTrialTasks())
      .unwrap()
      .catch(() => {
        dispatch(message(intl.formatMessage({ id: 'errors.generic-support' }), 'error'))
      })
  }, [dispatch, intl])

  useEffect(() => {
    if (openWithPlan.handle) {
      setSelectedProduct({ handle: openWithPlan.handle, isAnnual: Boolean(openWithPlan.isAnnual) })
      fetchTasks()
      // fetchCoupon()
    }
  }, [openWithPlan.handle, openWithPlan.isAnnual, fetchTasks])

  const selectedPlanDiscountPrice = useMemo(() => {
    const product = dispatch(getProductByHandle(selectedProduct.handle))
    if (product && coupon?.percent !== undefined) {
      const price = selectedProduct.isAnnual ? product.price.annual[currency] : product.price.monthly[currency]
      // eslint-disable-next-line no-magic-numbers
      const discount = price * (coupon.percent / 100)
      return (price - discount).toFixed(2)
    }
    return '0'
  }, [dispatch, selectedProduct.handle, selectedProduct.isAnnual, coupon.percent, currency])

  const setCopyRef = (element: HTMLElement | null) => {
    if (element) {
      if (clipboard.current) {
        (clipboard.current as any).destroy()
      }

      clipboard.current = new Clipboard(element, {
        text: getClipboardText,
        container: element
      })

      ; (clipboard.current as any).on('success', () => {
        dispatch(message(intl.formatMessage({ id: 'notifications.coupon-copied' })))
      })
    }
  }

  const closePopup = () => {
    dispatch(setTrialPopupOpen({ handle: null }))
  }

  const onUpgrade = () => {
    setUpgrading(true)

    if (isTrialActive) {
      closePopup()
      setUpgrading(false)
      navigate('/settings/subscription')
      return
    }

    if ([freePlan.handles.monthly, freePlan.handles.annual].includes(selectedProduct.handle)) {
      dispatch(downgradeFree()).unwrap()
        .then(() => {
          checkStatus$.current?.next(null)
          navigate('/settings/account')
        })
        .then(() => dispatch(getAccountInfo()).unwrap())
        .catch(() => {
          dispatch(message(intl.formatMessage({ id: 'errors.generic-support' }), 'error'))
        })
        .finally(() => {
          closePopup()
          setUpgrading(false)
        })
      return
    }

    const product = dispatch(getProductByHandle(selectedProduct.handle))
    dispatch(endTrial(selectedProduct.handle)).pipe(
      mergeMap(() => dispatch(upgradeProductV2(selectedProduct.handle))))
      .subscribe(
        () => {
          dispatch(message(intl.formatMessage(
            { id: 'notifications.upgrade-success' },
            { subscription: product?.name.toUpperCase() }
          ), 'success'))
          checkStatus$.current?.next(null)
          closePopup()
          setUpgrading(false)
        },
        error => {
          if (error.isChargify) {
            dispatch(message(intl.formatMessage(
              { id: 'upgrade-popup.notification-self-service' },
              {
                a: (text: string) => (
                  <a href={user?.account.selfServicePage} target="_blank">{text}</a>
                )
              } as any
            ), 'error', NOTIFICATION_DURATION_LONG))
          } else {
            dispatch(message(intl.formatMessage({ id: 'notifications.error-w-message' }, { error: error.message }) as string, 'error'))
          }
          closePopup()
          setUpgrading(false)
        }
      )
  }

  return (
    <PPDialog
      open={!loading && Boolean(openWithPlan.handle)}
      onClose={closePopup}
      zIndex={DIALOG_Z_INDEX}
      className={styles.dialog}
      title={
        <FormattedMessage
          id="popup.trial-end.now.title"
          values={{
            percent: coupon?.percent,
            color: (...text) => <span className={styles.hl}>{text}</span>
          }}
        />
      }
      footer={
        <Button color="primary" variant="contained" size="large" disabled={isUpgrading} onClick={onUpgrade}>
          <FormattedMessage id="popup.trial-end.now.action" />
        </Button>
      }
    >
      <div className={`${styles.content} ${styles.trial} ${styles.promo}`}>
        <div className={styles['end-now-box']}>
          <div className={styles['step-title']}>
            <FormattedMessage
              id="popup.trial-end.subtitle"
              values={{
                percent: coupon?.percent,
                br: <br />,
                color: (...text) => <span className={styles.hl}>{text}</span>
              }}
            />
          </div>
          {isTrialActive && (
            <Fragment>
              <div className={styles.text}>
                <FormattedMessage
                  id="popup.trial-end.copy-code"
                  values={{
                    code: coupon?.code,
                    button: (...text) => (
                      <Tooltip title={<FormattedMessage id="actions.click-to-copy" />}>
                        <span ref={setCopyRef}>
                          <span className={styles['hl-code']}>{text}</span>
                          <IconCopy fontSize="small" className={styles['icon-copy']} />
                        </span>
                      </Tooltip>
                    )
                  }}
                />
              </div>
              <div className={styles.text}>
                <FormattedMessage id="popup.trial-end.hint" />
              </div>
            </Fragment>
          )}
        </div>
      </div>
    </PPDialog>
  )
}
